﻿import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const PortaCallReporting = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daily, setDaily] = useState([]);
  const [tops, setTops] = useState(null);
  const [concurrency, setConcurrency] = useState(null);
  
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  useEffect(() => { load(); }, []); // eslint-disable-line
  
  async function load() {
    setLoading(true); setError('');
    try {
      const [dRes, tRes, cRes] = await Promise.all([
        apiFetch('/api/porta/call-daily?months=3'),
        apiFetch('/api/porta/call-tops'),
        apiFetch('/api/porta/call-concurrency?months=3')
      ]);
      if(!dRes.ok) throw new Error('Failed to fetch daily calls');
      if(!tRes.ok) throw new Error('Failed to fetch call tops');
      if(!cRes.ok) throw new Error('Failed to fetch concurrency');
      setDaily(await dRes.json());
      setTops(await tRes.json());
      setConcurrency(await cRes.json());
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message);
    }
    finally { setLoading(false);} 
  }

  const parseISODate = (s) => { if(!s) return null; const [y,m,d]=s.split('-').map(Number); return new Date(Date.UTC(y,(m||1)-1,d||1)); };
  const monthMeta = useMemo(()=>{ const now=new Date(); return [1,2,3].map(off=>{ const start=new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth()-off,1)); const end=new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth()+1,0)); return { start, days:end.getUTCDate(), label:start.toLocaleString(undefined,{ month:'short', year:'2-digit' }), key:`${start.getUTCFullYear()}-${start.getUTCMonth()+1}` }; }); }, []);
  const monthLabels = useMemo(()=> monthMeta.map(m=>m.label), [monthMeta]);

  // Calculate telephony efficiency health score (recent month vs target)
  const healthScore = useMemo(() => {
    if (!daily || daily.length === 0) return null;
    const recentMonthKey = monthMeta[0].key;
    const recentMonthData = daily.filter(d => { const dt = parseISODate(d.date); if(!dt) return false; return `${dt.getUTCFullYear()}-${dt.getUTCMonth()+1}` === recentMonthKey; });
    const totalCalls = recentMonthData.reduce((sum, d) => sum + (d.incoming || 0) + (d.outgoing || 0), 0);
    const totalCost = recentMonthData.reduce((sum, d) => sum + (d.cost || 0), 0);
    const avgCostPerCall = totalCalls > 0 ? totalCost / totalCalls : 0;
    // Target & bands: R0.85 benchmark (retain original proportional pattern)
    const targetCostPerCall = 0.85;
    let costScore = 0;
    if (avgCostPerCall <= targetCostPerCall) costScore = 100;
    else if (avgCostPerCall <= targetCostPerCall * 1.5) costScore = 70;
    else if (avgCostPerCall <= targetCostPerCall * 2) costScore = 40;
    else costScore = 20;

    const prevMonthKey = monthMeta[1]?.key; if(!prevMonthKey) return Math.round(costScore * 0.6 + 100 * 0.4);
    const prevMonthData = daily.filter(d => { const dt = parseISODate(d.date); if(!dt) return false; return `${dt.getUTCFullYear()}-${dt.getUTCMonth()+1}` === prevMonthKey; });
    const prevTotalCalls = prevMonthData.reduce((sum, d) => sum + (d.incoming || 0) + (d.outgoing || 0), 0);
    const volumeChangePct = prevTotalCalls > 0 ? ((totalCalls - prevTotalCalls) / prevTotalCalls) * 100 : 0;
    let stabilityScore = 100;
    if (Math.abs(volumeChangePct) > 50) stabilityScore = 20;
    else if (Math.abs(volumeChangePct) > 30) stabilityScore = 40;
    else if (Math.abs(volumeChangePct) > 15) stabilityScore = 70;
    const overall = (costScore * 0.6) + (stabilityScore * 0.4);
    return Math.round(overall);
  }, [daily, monthMeta]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 80) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 60) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 40) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 20) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const { outgoingSeries, incomingSeries, costSeries } = useMemo(()=>{ 
    const map=new Map(); 
    const keys=new Set(monthMeta.map(m=>m.key)); 
    for(const r of (Array.isArray(daily)?daily:[])){ 
      const dt=parseISODate(r.date); 
      if(!dt) continue; 
      const mk=`${dt.getUTCFullYear()}-${dt.getUTCMonth()+1}`; 
      if(!keys.has(mk)) continue; 
      const day=dt.getUTCDate(); 
      if(!map.has(mk)) map.set(mk,new Map()); 
      const byDay=map.get(mk); 
      const prev=byDay.get(day)||{ in:0,out:0,cost:0 }; 
      byDay.set(day,{ in:prev.in+(r.incoming||0), out:prev.out+(r.outgoing||0), cost:prev.cost+(r.cost||0) }); 
    }
    const maxDays=Math.max(...monthMeta.map(m=>m.days)); 
    const outgoingSeries=[], incomingSeries=[], costSeries=[]; 
    for(let d=1; d<=maxDays; d++){ 
      const rowOut={ day:d }, rowIn={ day:d }, rowCost={ day:d }; 
      monthMeta.forEach(m=>{ 
        const val=map.get(m.key)?.get(d); 
        rowOut[m.label]= d<=m.days ? (val?.out||0): null; 
        rowIn[m.label]= d<=m.days ? (val?.in||0): null; 
        rowCost[m.label]= d<=m.days ? (val?.cost||0): null; 
      }); 
      outgoingSeries.push(rowOut); 
      incomingSeries.push(rowIn); 
      costSeries.push(rowCost); 
    }
    return { outgoingSeries, incomingSeries, costSeries }; 
  }, [daily,monthMeta]);

  const { concurrencySeriesMulti, concurrencyMonthLabels } = useMemo(()=>{
    if(!concurrency) return { concurrencySeriesMulti: [], concurrencyMonthLabels: [] };

    if(Array.isArray(concurrency.months)){
      const monthsArr = [...concurrency.months];
      const monthInfo = monthsArr.map(m => {
        let daysInMonth = 31;
        if(m?.month){
          const [y,mm] = m.month.split('-').map(Number);
          if(y && mm){ daysInMonth = new Date(Date.UTC(y, mm, 0)).getUTCDate(); }
        }
        const dayMap = new Map();
        if(Array.isArray(m?.days)){
          m.days.forEach(d => { const dd = parseInt(d.date.split('-')[2],10); if(dd) dayMap.set(dd, d.peak||0); });
        }
        return { label: m.label, daysInMonth, dayMap };
      });
      const maxDays = Math.max(...monthInfo.map(mi => mi.daysInMonth));
      const series = [];
      for(let d=1; d<=maxDays; d++){
        const row = { day: d };
        monthInfo.forEach(mi => { row[mi.label] = d<=mi.daysInMonth ? (mi.dayMap.get(d) ?? 0) : null; });
        series.push(row);
      }
      return { concurrencySeriesMulti: series, concurrencyMonthLabels: monthInfo.map(mi=>mi.label) };
    }

    if(concurrency.days){
      let daysInMonth = 31; 
      if(concurrency.month){ const [y,m] = concurrency.month.split('-').map(Number); if(y && m){ daysInMonth = new Date(Date.UTC(y,m,0)).getUTCDate(); } }
      const dayMap = new Map();
      concurrency.days.forEach(d => { const dd=parseInt(d.date.split('-')[2],10); if(dd) dayMap.set(dd, { peak:d.peak, incoming:d.incoming, outgoing:d.outgoing, total:d.totalCalls }); });
      const series=[]; 
      for(let d=1; d<=daysInMonth; d++){ const val=dayMap.get(d); series.push({ day:d, peak: val?.peak||0, incoming: val?.incoming||0, outgoing: val?.outgoing||0, total: val?.total||0 }); }
      return { concurrencySeriesMulti: [], concurrencyMonthLabels: [] };
    }
    return { concurrencySeriesMulti: [], concurrencyMonthLabels: [] };
  }, [concurrency]);

  // MOVE useMemo BEFORE CONDITIONAL RETURNS TO AVOID HOOK ORDER MISMATCH
  const globalTotals = useMemo(()=>{
    if(!Array.isArray(daily) || daily.length===0) return { calls:0,cost:0,avg:0 };
    const calls = daily.reduce((s,d)=> s + (d.incoming||0) + (d.outgoing||0),0);
    const cost = daily.reduce((s,d)=> s + (d.cost||0),0);
    return { calls, cost, avg: calls>0 ? cost/calls : 0 };
  },[daily]);

  const colors=[primaryColor,'#FF9900', secondaryColor];
  const concurrencyColors=['#d32f2f','#0288d1','#8e24aa'];
  const formatCurrency = (v) => { try { return new Intl.NumberFormat(undefined,{ style:'currency', currency:'ZAR', maximumFractionDigits:2 }).format(v||0); } catch (catchError) { Sentry.captureException(catchError); return (v??0).toFixed(2);} };
  const CustomTooltip = ({ active,payload,label }) => active? (<div style={{ background:'#fff', border:'1px solid #ccc', padding:6, borderRadius:4, fontSize:11 }}><div style={{ fontWeight:600 }}>Day {label}</div>{payload.map((p,i)=>(<div key={i} style={{ color:p.color }}>{p.name}: {p.value}</div>))}</div>):null;
  const CustomTooltipCurrency = ({ active,payload,label }) => active? (<div style={{ background:'#fff', border:'1px solid #ccc', padding:6, borderRadius:4, fontSize:11 }}><div style={{ fontWeight:600 }}>Day {label}</div>{payload.map((p,i)=>(<div key={i} style={{ color:p.color }}>{p.name}: {formatCurrency(p.value)}</div>))}</div>):null;
  const prettyMsisdn = (n) => { if(!n) return ''; let s=String(n).trim().replace(/^[+]?27/,'0').replace(/\D/g,''); if(s.length===10) return s.replace(/(\d{3})(\d{3})(\d{4})/,'$1 $2 $3'); return s; };
  const niceDateTime = (v)=>{ if(!v) return ''; const dt=new Date(v); const m=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; const pad=n=>String(n).padStart(2,'0'); return `${dt.getDate()} ${m[dt.getMonth()]} ${dt.getFullYear()}, ${pad(dt.getHours())}:${pad(dt.getMinutes())}`; };
  const lastMonthLabel = monthLabels[0] || 'Prev';

  if (loading) return <HexagonLoader/>; 
  if (error) return <div style={{ color:'red' }}>{error}</div>;

  // Summary metrics for latest month
  const recentMonthKey = monthMeta[0].key;
  const recentMonthData = daily.filter(d => { const dt = parseISODate(d.date); if(!dt) return false; return `${dt.getUTCFullYear()}-${dt.getUTCMonth()+1}` === recentMonthKey; });
  const totalCalls = recentMonthData.reduce((sum, d) => sum + (d.incoming || 0) + (d.outgoing || 0), 0);
  const totalCost = recentMonthData.reduce((sum, d) => sum + (d.cost || 0), 0);
  const avgCostPerCall = totalCalls > 0 ? totalCost / totalCalls : 0;
  const highCostCallsCount = (tops?.highCostCalls || []).length;
  
  const scoreInterp = getScoreInterpretation(healthScore);
  
  const llmProxyText = [
    `Telephony Efficiency Score: ${healthScore}/100`,
    `Total Calls: ${totalCalls}`,
    `Total Cost: ${formatCurrency(totalCost)}`,
    `Avg Cost/Call: ${formatCurrency(avgCostPerCall)}`,
    `High-Cost Calls: ${highCostCallsCount}`,
    `Global Avg Cost/Call: ${formatCurrency(globalTotals.avg)}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper porta-call-reporting">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0, marginBottom: 12 }}>Telephony Cost & Usage Efficiency</h2>

      {/* Executive Summary Banner */}
      {daily.length > 0 && (
        <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
          <div className="exec-banner-layout">
            <div className="exec-banner-score">
              <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
                {healthScore != null ? healthScore : '–'}
                <span className="exec-banner-suffix">/ 100</span>
              </div>
              <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
              <div className="exec-banner-sublabel">Telephony Efficiency ({lastMonthLabel})</div>
            </div>
            <div className="exec-banner-body">
              <div className="exec-banner-text">
                <strong>What this means:</strong> Your telephony system processed <strong>{totalCalls.toLocaleString()} calls</strong> at <strong>{formatCurrency(avgCostPerCall)} average cost/call</strong> totaling {formatCurrency(totalCost)} in {lastMonthLabel}.
                {avgCostPerCall <= 0.85 && ' Excellent cost efficiency.'}
                {avgCostPerCall > 0.85 && avgCostPerCall <= 1.70 && ' Good cost management with room for optimization.'}
                {avgCostPerCall > 1.70 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> High per-call costs - review carrier rates and call routing.</span>}
                {highCostCallsCount > 10 && <span style={{ color: '#ff5722', fontWeight: 600 }}> {highCostCallsCount} high-cost calls (≥R50) detected - potential international calls or routing issues.</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      {daily.length > 0 && (
        <div className="metrics-row">
          <div className="dashboard-card metric-card" title="Total voice calls processed this month">
            <div className="metric-label">Total Calls</div>
            <div className="metric-value--md" style={{ fontWeight:700 }}>{totalCalls.toLocaleString()}</div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>This month</div>
          </div>
          <div className="dashboard-card metric-card" title="Total telephony expenditure this month">
            <div className="metric-label">Total Cost</div>
            <div className="metric-value--md" style={{ fontWeight:700 }}>{formatCurrency(totalCost)}</div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>This month</div>
          </div>
          <div className="dashboard-card metric-card" title="Average cost per call - efficiency metric">
            <div className="metric-label">Avg Cost / Call</div>
            <div className="metric-value--sm" style={{ color: avgCostPerCall <= 0.85 ? '#2e7d32' : avgCostPerCall <= 1.70 ? '#ffa000' : '#d32f2f' }}>
              {formatCurrency(avgCostPerCall)}
            </div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>Target: R0.85</div>
          </div>
          <div className="dashboard-card metric-card" title="Calls exceeding R50 - potential anomalies">
            <div className="metric-label">High-Cost Calls</div>
            <div className="metric-value--md" style={{ fontWeight:700, color: highCostCallsCount > 10 ? '#d32f2f' : highCostCallsCount > 5 ? '#ffa000' : '#666' }}>
              {highCostCallsCount}
            </div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>≥R50 per call</div>
          </div>
          <div className="dashboard-card metric-card" title="Average cost per call across all loaded months">
            <div className="metric-label">Global Avg / Call</div>
            <div className="metric-value--sm">{formatCurrency(globalTotals.avg)}</div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>Across 3 months</div>
          </div>
        </div>
      )}

      {/* Cost Trend */}
      <div className="dashboard-card chart-card">
        <h4 className="chart-title">Daily Cost Comparison (Last 3 Months)</h4>
        <div className="chart-subtitle">Track spending patterns and identify cost spikes</div>
        <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={150} minHeight={150}>
          <LineChart data={costSeries} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis tickFormatter={(v) => formatCurrency(v)} />
            <Tooltip content={<CustomTooltipCurrency />} />
            <Legend />
            {monthLabels.map((lbl, idx) => (<Line key={lbl} type="monotone" dataKey={lbl} stroke={colors[idx] || '#3f51b5'} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Concurrency */}
      {concurrencySeriesMulti.length > 0 && (
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Peak Concurrent Calls (3-Month Comparison)</h4>
          <div className="chart-subtitle">Maximum simultaneous calls - indicates capacity needs</div>
          <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={150} minHeight={150}>
            <LineChart data={concurrencySeriesMulti} margin={{ top:4, right:12, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis allowDecimals={false} />
              <Tooltip content={<CustomTooltip/>} />
              <Legend />
              {concurrencyMonthLabels.map((lbl,idx)=>(<Line key={lbl} type="monotone" dataKey={lbl} name={lbl} stroke={concurrencyColors[idx]||primaryColor} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Call Volume Trends */}
      <div className="dashboard-grid compact" style={{ marginTop:4 }}>
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Inbound Call Volume</h4>
          <div className="chart-subtitle">Incoming calls to organization</div>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={150} minHeight={150}>
            <LineChart data={incomingSeries} margin={{ top:4, right:12, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis allowDecimals={false} />
              <Tooltip content={<CustomTooltip/>} />
              <Legend />
              {monthLabels.map((lbl,idx)=>(<Line key={lbl} type="monotone" dataKey={lbl} stroke={colors[idx]||'#82ca9d'} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Outbound Call Volume</h4>
          <div className="chart-subtitle">Outgoing calls from organization</div>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={150} minHeight={150}>
            <LineChart data={outgoingSeries} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {monthLabels.map((lbl, idx) => (<Line key={lbl} type="monotone" dataKey={lbl} stroke={colors[idx] || '#8884d8'} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Usage Details */}
      {tops && (
        <div className="dashboard-grid compact" style={{ marginTop:12 }}>
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Top 5 Longest Outbound Calls ({lastMonthLabel})</h4>
            <div className="chart-subtitle">Longest duration outbound calls - potential cost concerns</div>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign:'left', padding: '4px 6px' }}>From</th>
                  <th style={{ textAlign:'left', padding: '4px 6px' }}>To</th>
                  <th style={{ padding: '4px 6px' }}>Duration</th>
                  <th style={{ padding: '4px 6px' }}>Start</th>
                  <th style={{ textAlign:'right', padding: '4px 6px' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {(tops.topLongestOutbound||[]).map((c,idx)=>(
                  <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign:'left', padding: '4px 6px' }}>{prettyMsisdn(c.from)}</td>
                    <td style={{ textAlign:'left', padding: '4px 6px' }}>{prettyMsisdn(c.to)}</td>
                    <td style={{ textAlign:'right', padding: '4px 6px' }}>{Math.floor((c.durationSeconds||0)/60)}m {(c.durationSeconds||0)%60}s</td>
                    <td style={{ padding: '4px 6px', fontSize: 10 }}>{niceDateTime(c.startUtc)}</td>
                    <td style={{ textAlign:'right', fontWeight:600, padding: '4px 6px' }}>{formatCurrency(c.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Top 5 Most Called Numbers ({lastMonthLabel})</h4>
            <div className="chart-subtitle">Frequently called external numbers</div>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign:'left', padding: '4px 6px' }}>Number</th>
                  <th style={{ textAlign: 'right', padding: '4px 6px' }}>Call Count</th>
                </tr>
              </thead>
              <tbody>
                {(tops.topMostCalledNumbers||[]).map((r,idx)=>(
                  <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign:'left', padding: '4px 6px', fontWeight: 500 }}>{prettyMsisdn(r.number)||'-'}</td>
                    <td style={{ textAlign:'right', fontWeight:600, padding: '4px 6px' }}>{r.count||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* High Cost Calls */}
      {tops && Array.isArray(tops.highCostCalls) && tops.highCostCalls.length > 0 && (
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">High-Cost Calls ≥ R50 ({lastMonthLabel})</h4>
          <div className="chart-subtitle">Calls exceeding R50 - review for international calls, premium numbers, or routing errors</div>
          <div style={{ maxHeight:220, overflowY:'auto' }}>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{textAlign:'left', padding: '4px 6px' }}>Direction</th>
                  <th style={{ textAlign:'left', padding: '4px 6px' }}>From</th>
                  <th style={{ textAlign:'left', padding: '4px 6px' }}>To</th>
                  <th style={{ padding: '4px 6px' }}>Duration</th>
                  <th style={{ padding: '4px 6px' }}>Start</th>
                  <th style={{ textAlign:'right', padding: '4px 6px' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {tops.highCostCalls.map((c,idx)=>(
                  <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textTransform:'capitalize', padding: '4px 6px' }}>{c.direction}</td>
                    <td style={{ padding: '4px 6px' }}>{prettyMsisdn(c.from)}</td>
                    <td style={{ padding: '4px 6px' }}>{prettyMsisdn(c.to)}</td>
                    <td style={{ textAlign:'right', padding: '4px 6px' }}>{Math.floor((c.durationSeconds||0)/60)}m {(c.durationSeconds||0)%60}s</td>
                    <td style={{ padding: '4px 6px', fontSize: 10 }}>{niceDateTime(c.startUtc)}</td>
                    <td style={{ textAlign:'right', fontWeight:600, color: '#d32f2f', padding: '4px 6px' }}>{formatCurrency(c.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding Telephony Metrics:</strong>
        <ul>
          <li><strong>Telephony Efficiency Score:</strong> Cost Efficiency (60%) + Volume Stability (40%). Target is 80%+ for optimized telephony operations.</li>
          <li><strong>Cost Efficiency:</strong> Target {'<'} R0.85/call. Higher suggests international calls, poor routing, or expensive carrier rates.</li>
          <li><strong>Volume Stability:</strong> Large month-over-month swings may indicate business changes or system issues requiring investigation.</li>
          <li><strong>High-Cost Calls:</strong> Calls {'>'} R50 - review for international calls, premium rate numbers, or misrouted calls. May indicate policy violations.</li>
          <li><strong>Peak Concurrency:</strong> Maximum simultaneous calls. Use for capacity planning - ensure phone system can handle peak loads.</li>
          <li><strong>Call Patterns:</strong> Compare inbound vs outbound. High outbound may indicate sales activity. High inbound suggests customer service load.</li>
        </ul>
        {avgCostPerCall > 1.70 && (
          <div className="action-callout">
            <strong>Action Required:</strong> Average cost of {formatCurrency(avgCostPerCall)}/call is high relative to the R0.85 target. Review: international calling policies, carrier rate negotiation, call routing optimization (use cheapest carrier), auto-attendant to reduce transfers, and consider VoIP alternatives for long-distance calls.
          </div>
        )}
        {highCostCallsCount > 10 && (
          <div className="action-callout">
            <strong>Investigation Needed:</strong> {highCostCallsCount} calls exceeded R50 each. Review call logs above for: international calls (may need policy enforcement), premium rate numbers (scams/unapproved services), routing errors, or unusually long call durations. Each high-cost call should be justified.
          </div>
        )}
      </div>

      <div className="data-source">
        Data Source: Porta telephony platform call detail records (last 3 months). Costs in ZAR. International calls and premium numbers incur higher per-minute rates. Peak concurrency indicates maximum simultaneous call capacity requirements.
      </div>
    </div>
  );
};

export default PortaCallReporting;
